// src/utils/ftcCompliance.js
// FTC compliance layer for enhanced-tier clients (Morgan Auto Group, etc.)
// - FTC_CHECKLIST: the vendor certification checklist as data
// - runAutoChecks: deterministic validations the system can verify itself
// - buildFtcDisclaimer: generates the FTC-enhanced disclaimer from order data

// ---------------------------------------------------------------------------
// Shared math helpers (same formula as ViewOrderPage.calculateMonthlyPayment)
// ---------------------------------------------------------------------------

export const calcMonthlyPayment = (salePrice, downPct, apr, months) => {
  const price = parseFloat(salePrice) || 0;
  const downPayment = price * ((parseFloat(downPct) || 0) / 100);
  const principal = price - downPayment;
  const monthlyRate = (parseFloat(apr) || 0) / 100 / 12;
  if (!months || !principal) return 0;
  const payment = monthlyRate === 0
    ? principal / months
    : (monthlyRate * principal) / (1 - Math.pow(1 + monthlyRate, -months));
  return isNaN(payment) ? 0 : Math.round(payment);
};

// Rate per $1,000 financed (e.g. 5.9% / 84mo => 14.54)
export const calcPerThousand = (apr, months) => {
  const monthlyRate = (parseFloat(apr) || 0) / 100 / 12;
  if (!months) return 0;
  const payment = monthlyRate === 0
    ? 1000 / months
    : (monthlyRate * 1000) / (1 - Math.pow(1 + monthlyRate, -months));
  return isNaN(payment) ? 0 : Math.round(payment * 100) / 100;
};

const fmtMoney = (val) => {
  const num = parseFloat(String(val).replace(/[$,]/g, ''));
  if (isNaN(num)) return val || '';
  return num.toLocaleString('en-US');
};

const fmtLongDate = (rawDate) => {
  if (!rawDate) return '';
  const [year, month, day] = String(rawDate).split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (isNaN(date)) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const getMake = (v) => (v.make === 'Other' ? (v.customMake || v.make) : v.make);

// ---------------------------------------------------------------------------
// FTC CHECKLIST (from DIRECT_MAIL_FTC_COMPLIANCE_CHECKLIST)
// type: 'auto'  -> verified by runAutoChecks (key = item id)
//       'manual'-> human checkbox with attribution
// tiers: which compliance tiers require the item.
//        Omitted = ['ftc-enhanced'] only. 'BOTH' below = medium + enhanced.
// ---------------------------------------------------------------------------

const BOTH = ['medium', 'ftc-enhanced'];

export const FTC_CHECKLIST = [
  {
    section: '1. Price & Payment Accuracy',
    items: [
      { id: 'price_full', type: 'manual', text: 'All advertised prices reflect the full purchase price' },
      { id: 'fees_included', type: 'auto', text: 'All mandatory dealer fees are INCLUDED in advertised price and/or payment (pre-delivery, doc, private tag, electronic filing)' },
      { id: 'fees_not_added_later', type: 'manual', text: 'No required fees are excluded and added later' },
      { id: 'exclusions_ok', type: 'auto', text: 'Only tax, title/registration, and government fees are excluded' },
    ],
  },
  {
    section: '2. Lease Payment Compliance',
    appliesTo: 'lease',
    items: [
      { id: 'lease_math', type: 'auto', tiers: BOTH, text: 'Monthly payment is accurate and mathematically consistent with MSRP' },
      { id: 'lease_term', type: 'auto', tiers: BOTH, text: 'Term length clearly disclosed' },
      { id: 'lease_due_at_signing', type: 'auto', tiers: BOTH, text: 'Total due at signing clearly disclosed (not just down payment)' },
      { id: 'lease_mileage', type: 'auto', text: 'Mileage limits clearly disclosed' },
      { id: 'lease_acq_fee', type: 'auto', text: 'Acquisition fee disclosed (included or excluded)' },
      { id: 'lease_wqb', type: 'auto', text: '"For well-qualified buyers/lessees" is present' },
      { id: 'lease_tax', type: 'auto', text: 'Tax treatment clearly stated (included or excluded)' },
    ],
  },
  {
    section: '3. Finance / APR Disclosure',
    appliesTo: 'finance',
    items: [
      { id: 'apr_disclosed', type: 'auto', text: 'APR disclosed if referenced' },
      { id: 'finance_term', type: 'auto', text: 'Term length disclosed' },
      { id: 'finance_down', type: 'auto', text: 'Down payment disclosed (if required)' },
      { id: 'amount_financed', type: 'auto', text: 'Amount financed disclosed' },
      { id: 'finance_math', type: 'auto', tiers: BOTH, text: 'Payment math verified ($ per month per $1,000 financed)' },
      { id: 'no_misleading_zero', type: 'manual', tiers: BOTH, text: 'No misleading claims (e.g., "0%" without qualification)' },
    ],
  },
  {
    section: '4. Rebates & Incentives',
    items: [
      { id: 'rebate_eligibility', type: 'manual', text: 'Eligibility requirements clearly disclosed; conditional rebates (conquest, loyalty, military, trade assistance) NOT presented as universally available' },
    ],
  },
  {
    section: '5. Advertising Claims Validation',
    items: [
      { id: 'as_low_as', type: 'manual', tiers: BOTH, text: '"As low as" / "up to" claims are legitimate, accurate, and substantiated' },
      { id: 'inventory_available', type: 'manual', tiers: BOTH, text: 'Inventory supporting claims is available at time of drop' },
    ],
  },
  {
    section: '6. Vehicle Identification',
    items: [
      { id: 'stock_or_vin', type: 'auto', tiers: BOTH, text: 'Stock number or VIN included for every advertised vehicle (VIN preferred)' },
      { id: 'ymm', type: 'auto', tiers: BOTH, text: 'Year, make, model clearly identified' },
      { id: 'in_stock', type: 'manual', tiers: BOTH, text: 'Vehicles advertised are in-stock or legitimately available' },
    ],
  },
  {
    section: '7. Disclaimers & Fine Print',
    items: [
      { id: 'legible', type: 'manual', tiers: BOTH, text: 'All disclaimers are legible on the artwork (no micro-font)' },
      { id: 'placement', type: 'manual', tiers: BOTH, text: 'Disclaimers are placed near the relevant offer' },
      { id: 'no_contradiction', type: 'manual', tiers: BOTH, text: 'Fine print does NOT contradict headline claims' },
      { id: 'fee_sentence', type: 'auto', text: 'Required disclosure present: "Price includes $___ dealer fee. Excludes tax, tag, title, and registration."' },
    ],
  },
  {
    section: '8. High-Risk Language Review',
    items: [
      { id: 'no_prohibited', type: 'manual', tiers: BOTH, text: 'No "Guaranteed approval" / "Everyone approved" used (not permitted)' },
      { id: 'risky_substantiated', type: 'manual', tiers: BOTH, text: '"$0 down" / "No money down" / "Sign and drive" / "Free" claims include proper disclosures and conditions' },
    ],
  },
  {
    section: '9. Promotional Urgency Claims',
    items: [
      { id: 'urgency_truthful', type: 'manual', tiers: BOTH, text: '"Limited time" / urgency language is truthful' },
      { id: 'expiration_real', type: 'auto', tiers: BOTH, text: 'Offer expiration dates are real and supportable' },
    ],
  },
  {
    section: '10. Credit & Approval Language',
    items: [
      { id: 'wqb_everywhere', type: 'auto', text: '"For well-qualified buyers" included wherever applicable' },
      { id: 'no_deceptive_credit', type: 'manual', tiers: BOTH, text: 'No deceptive credit approval claims used' },
    ],
  },
  {
    section: '11. Trade-In Value',
    items: [
      { id: 'kbb_reference', type: 'manual', tiers: BOTH, text: 'Trade-in value claims reference a recognized source (KBB/Black Book/NADA) with edition, zip code, and condition grade for "up to" claims' },
      { id: 'negative_equity', type: 'manual', text: 'Negative equity disclosure present if advertising payoff or trade offers' },
    ],
  },
  {
    section: '12. Final Quality Control',
    items: [
      { id: 'math_verified', type: 'auto', tiers: BOTH, text: 'All math (pricing and payments) has been verified' },
      { id: 'disclosures_match', type: 'manual', tiers: BOTH, text: 'All disclosures match the advertised offer exactly on the artwork' },
      { id: 'final_review', type: 'manual', text: 'Final review completed by a Regional Marketing Director and/or Legal' },
    ],
  },
];

// Which tiers require a checklist item (default: enhanced only)
export const itemAppliesToTier = (item, tier) =>
  (item.tiers || ['ftc-enhanced']).includes(tier);

// Checklist filtered for a tier (sections with no applicable items are dropped)
export const getChecklistForTier = (tier) =>
  FTC_CHECKLIST
    .map((s) => ({ ...s, items: s.items.filter((i) => itemAppliesToTier(i, tier)) }))
    .filter((s) => s.items.length > 0);

export const getAllChecklistItems = (tier = 'ftc-enhanced') =>
  getChecklistForTier(tier)
    .flatMap((s) => s.items.map((i) => ({ ...i, section: s.section, appliesTo: s.appliesTo })));

// ---------------------------------------------------------------------------
// AUTO CHECKS - deterministic validations, no AI required.
// Returns { [itemId]: { pass: bool|null, message: string } }
// pass === null means "not applicable" for this order.
// ---------------------------------------------------------------------------

export const runAutoChecks = (vehicles = [], prizes = [], orderDetails = {}, dealershipInfo = {}, tier = 'ftc-enhanced') => {
  const results = {};
  const fees = dealershipInfo?.complianceFees || {};
  const feeTotal =
    (parseFloat(fees.preDeliveryFee) || 0) +
    (parseFloat(fees.privateTagFee) || 0) +
    (parseFloat(fees.electronicFilingFee) || 0) +
    (parseFloat(fees.docFee) || 0);

  const financeVehicles = vehicles.filter((v) => v.financeType !== 'lease' && (v.make || v.model));
  const leaseVehicles = vehicles.filter((v) => v.financeType === 'lease' && (v.make || v.model));
  const activeVehicles = [...financeVehicles, ...leaseVehicles];

  const label = (v, i) => `${v.year || '?'} ${getMake(v) || '?'} ${v.model || '?'}`.trim() || `Vehicle ${i + 1}`;
  const na = { pass: null, message: 'Not applicable to this order' };

  // --- 1. Fees ---
  results.fees_included = feeTotal > 0
    ? { pass: true, message: `Dealer fees configured on client profile ($${fmtMoney(feeTotal)} total) and written into every offer` }
    : { pass: false, message: 'No dealer fees configured on the client profile - set Pre-Delivery / Private Tag / Electronic Filing / Doc fees in the client\'s Compliance settings' };
  results.exclusions_ok = { pass: true, message: 'Generated disclaimer only excludes tax, tag, title, registration, and government fees' };
  results.fee_sentence = results.fees_included.pass
    ? { pass: true, message: 'Fee-inclusion sentence with dollar amounts is generated in every offer' }
    : { pass: false, message: 'Fee sentence cannot be generated until client fees are configured' };

  // --- 2. Lease checks ---
  if (leaseVehicles.length === 0) {
    ['lease_math', 'lease_term', 'lease_due_at_signing', 'lease_mileage', 'lease_acq_fee', 'lease_wqb', 'lease_tax']
      .forEach((k) => { results[k] = na; });
  } else {
    const missTerm = leaseVehicles.filter((v) => !v.leaseMonths);
    results.lease_term = missTerm.length
      ? { pass: false, message: `Missing lease term: ${missTerm.map(label).join(', ')}` }
      : { pass: true, message: 'Lease term disclosed for all lease vehicles' };

    const missDue = leaseVehicles.filter((v) => !v.totalDueAtSigning);
    results.lease_due_at_signing = missDue.length
      ? { pass: false, message: `Missing total due at signing: ${missDue.map(label).join(', ')}` }
      : { pass: true, message: 'Total due at signing disclosed for all lease vehicles' };

    const missMiles = leaseVehicles.filter((v) => !v.milesPerYear);
    results.lease_mileage = missMiles.length
      ? { pass: false, message: `Missing miles/year: ${missMiles.map(label).join(', ')} (enter in Compliance Details)` }
      : { pass: true, message: 'Mileage limits disclosed for all lease vehicles' };

    const missAcq = leaseVehicles.filter((v) => v.acquisitionFee === undefined || v.acquisitionFee === '');
    results.lease_acq_fee = missAcq.length
      ? { pass: false, message: `Missing acquisition fee: ${missAcq.map(label).join(', ')} (enter 0 if none)` }
      : { pass: true, message: 'Acquisition fee disclosed for all lease vehicles' };

    // Sanity: total lease cost (payments + due at signing) normally runs ~20%–60%
    // of MSRP over the term. Catches MSRP typos like a $399/mo Palisade lease
    // listed at "$25,450 MSRP" (real MSRP $40k+): total cost = 72% of MSRP → flag.
    const suspicious = [];
    const missingMsrp = [];
    leaseVehicles.forEach((v, i) => {
      const msrp = parseFloat(String(v.msrp || '').replace(/[$,]/g, ''));
      const dest = parseFloat(String(v.destinationCharge || '').replace(/[$,]/g, '')) || 0;
      const pmt = parseFloat(String(v.salePrice || '').replace(/[$,]/g, ''));
      const due = parseFloat(String(v.totalDueAtSigning || '').replace(/[$,]/g, '')) || 0;
      const term = parseInt(v.leaseMonths, 10) || 0;
      if (!msrp) { missingMsrp.push(label(v, i)); return; }
      if (!pmt || !term) return;
      const totalCost = pmt * term + due;
      const ratio = totalCost / (msrp + dest);
      if (ratio < 0.2 || ratio > 0.6) {
        suspicious.push(`${label(v, i)} - total lease cost $${fmtMoney(totalCost)} is ${Math.round(ratio * 100)}% of the $${fmtMoney(msrp + dest)} MSRP (expected ~20–60%); check MSRP or payment for typos`);
      }
    });
    if (missingMsrp.length) {
      // Enhanced requires MSRP; on medium the sanity check is best-effort only
      results.lease_math = tier === 'ftc-enhanced'
        ? { pass: false, message: `Missing MSRP (needed for payment sanity check): ${missingMsrp.join(', ')}` }
        : (suspicious.length
            ? { pass: false, message: `Payment/MSRP looks inconsistent - check for typos: ${suspicious.join('; ')}` }
            : { pass: null, message: `MSRP not entered - lease payment sanity check skipped (${missingMsrp.join(', ')})` });
    } else if (suspicious.length) {
      results.lease_math = { pass: false, message: `Payment/MSRP looks inconsistent - check for typos: ${suspicious.join('; ')}` };
    } else {
      results.lease_math = { pass: true, message: 'Lease payments are consistent with MSRPs' };
    }

    results.lease_wqb = { pass: true, message: '"Very well-qualified lessees" language is generated in every lease offer' };
    results.lease_tax = { pass: true, message: '"Excludes tax, title, license" is generated in every lease offer' };
  }

  // --- 3. Finance checks ---
  if (financeVehicles.length === 0) {
    ['apr_disclosed', 'finance_term', 'finance_down', 'amount_financed', 'finance_math']
      .forEach((k) => { results[k] = na; });
  } else {
    const missApr = financeVehicles.filter((v) => !v.apr && v.apr !== 0);
    results.apr_disclosed = missApr.length
      ? { pass: false, message: `Missing APR: ${missApr.map(label).join(', ')}` }
      : { pass: true, message: 'APR disclosed for all finance vehicles' };

    const missTerm = financeVehicles.filter((v) => !v.months);
    results.finance_term = missTerm.length
      ? { pass: false, message: `Missing term: ${missTerm.map(label).join(', ')}` }
      : { pass: true, message: 'Term length disclosed for all finance vehicles' };

    const missDown = financeVehicles.filter((v) => v.down === undefined || v.down === '');
    results.finance_down = missDown.length
      ? { pass: false, message: `Missing down payment %: ${missDown.map(label).join(', ')}` }
      : { pass: true, message: 'Down payment disclosed for all finance vehicles' };

    results.amount_financed = { pass: true, message: 'Amount financed is calculated and written into every finance offer' };

    // Math: payment is derived from price/down/APR/term with the standard formula,
    // and the per-$1,000 factor is computed from the same APR/term, so they can
    // never disagree. Verify inputs are complete instead.
    const incomplete = financeVehicles.filter((v) => !v.salePrice || !v.months || (!v.apr && v.apr !== 0));
    results.finance_math = incomplete.length
      ? { pass: false, message: `Cannot verify payment math - missing price/APR/term: ${incomplete.map(label).join(', ')}` }
      : { pass: true, message: `Payment math verified (${financeVehicles.map((v) => `$${calcMonthlyPayment(v.salePrice, v.down, v.apr, v.months)}/mo @ $${calcPerThousand(v.apr, v.months)} per $1,000`).join('; ')})` };
  }

  // --- 6. Vehicle identification ---
  if (activeVehicles.length === 0) {
    results.stock_or_vin = { pass: false, message: 'No vehicles entered on this order' };
    results.ymm = { pass: false, message: 'No vehicles entered on this order' };
  } else {
    const missId = activeVehicles.filter((v) => !v.stockNumber && !v.vin);
    results.stock_or_vin = missId.length
      ? { pass: false, message: `Missing Stock# and VIN: ${missId.map(label).join(', ')}` }
      : { pass: true, message: 'Every vehicle has a Stock# or VIN' };

    const missYmm = activeVehicles.filter((v) => !v.year || !v.make || !v.model);
    results.ymm = missYmm.length
      ? { pass: false, message: `Missing year/make/model on ${missYmm.length} vehicle(s)` }
      : { pass: true, message: 'Year, make, and model identified for every vehicle' };
  }

  // --- 9. Expiration date ---
  const validDrops = (orderDetails?.drops || []).filter((d) => d.startDate && d.endDate);
  if (!validDrops.length) {
    results.expiration_real = { pass: false, message: 'No drop dates with start and end set - offer expiration cannot be generated' };
  } else {
    const end = validDrops[validDrops.length - 1].endDate;
    const [y, m, d] = end.split('-').map(Number);
    const endDate = new Date(y, m - 1, d);
    const start = validDrops[0].startDate;
    const [sy, sm, sd] = start.split('-').map(Number);
    const startDate = new Date(sy, sm - 1, sd);
    results.expiration_real = endDate >= startDate
      ? { pass: true, message: `Offer expiration set: all offers end ${fmtLongDate(end)}` }
      : { pass: false, message: 'Drop end date is before the start date' };
  }

  // --- 10. Well-qualified buyers ---
  results.wqb_everywhere = { pass: true, message: '"For well-qualified buyers only" is generated in every offer' };

  // --- 12. Math verified rollup ---
  const mathParts = [results.finance_math, results.lease_math].filter((r) => r && r.pass !== null);
  results.math_verified = mathParts.length === 0
    ? { pass: false, message: 'No vehicle offers to verify' }
    : mathParts.every((r) => r.pass)
      ? { pass: true, message: 'All pricing and payment math verified' }
      : { pass: false, message: 'One or more math checks failed (see sections 2 and 3)' };

  return results;
};

// ---------------------------------------------------------------------------
// Checklist completion - auto items must pass, manual items must be checked.
// checklistState: { [itemId]: { checked, by, at } }
// ---------------------------------------------------------------------------

export const getChecklistProgress = (checklistState = {}, autoResults = {}, tier = 'ftc-enhanced') => {
  const items = getAllChecklistItems(tier);
  let required = 0;
  let done = 0;
  const outstanding = [];
  items.forEach((item) => {
    if (item.type === 'auto') {
      const res = autoResults[item.id];
      if (!res || res.pass === null) return; // N/A - not required
      required += 1;
      if (res.pass) done += 1;
      else outstanding.push(item.text);
    } else {
      required += 1;
      if (checklistState[item.id]?.checked) done += 1;
      else outstanding.push(item.text);
    }
  });
  return { required, done, complete: done === required && required > 0, outstanding };
};

// ---------------------------------------------------------------------------
// FTC-ENHANCED DISCLAIMER BUILDER
// Formats mirror the approved Morgan-style disclaimers
// (SRMG-705 Mazda Gainesville, SRMG-717 Toyota Tampa Bay, SRMG-718 Mazda Fort Myers)
// ---------------------------------------------------------------------------

export const buildFtcDisclaimer = ({ vehicles = [], prizes = [], orderDetails = {}, dealershipInfo = {} }) => {
  const fees = dealershipInfo?.complianceFees || {};
  const preDelivery = parseFloat(fees.preDeliveryFee) || 0;
  const privateTag = parseFloat(fees.privateTagFee) || 0;
  const eFiling = parseFloat(fees.electronicFilingFee) || 0;
  const docFee = parseFloat(fees.docFee) || 0;

  const validDrops = (orderDetails?.drops || []).filter((d) => d.startDate && d.endDate);
  const dropStart = fmtLongDate(validDrops[0]?.startDate) || 'Start Date';
  const dropEnd = fmtLongDate(validDrops[validDrops.length - 1]?.endDate) || 'End Date';

  // Per-offer fee sentence (checklist section 7 required example)
  const perOfferFeeSentence = () => {
    const named = [];
    if (preDelivery) named.push(`a pre-delivery service fee of $${fmtMoney(preDelivery)}`);
    if (privateTag) named.push(`a Private Tag Agency Fee of $${fmtMoney(privateTag)}`);
    if (eFiling) named.push(`an Electronic Registration Filing Fee of $${fmtMoney(eFiling)}`);
    if (docFee) named.push(`a dealer/doc fee of $${fmtMoney(docFee)}`);
    if (!named.length) return 'Price includes $<FEE> dealer fee. Excludes tax, tag, title, and registration.';
    const joined = named.length > 1
      ? `${named.slice(0, -1).join(', ')}, and ${named[named.length - 1]}`
      : named[0];
    return `Advertised Price includes ${joined}. Price listed does not include sales tax, tag, title, registration, and any other government-related fees.`;
  };

  const usable = vehicles.filter((v) => {
    if (v.financeType === 'lease') return v.make && v.model && v.salePrice && v.leaseMonths;
    return v.make && v.model && v.salePrice && v.months;
  });

  const vehicleLines = usable.map((v, i) => {
    const make = getMake(v);
    const trim = v.trim ? ` ${v.trim}` : '';
    if (v.financeType === 'lease') {
      // Lease format (Mazda Gainesville pattern)
      const idPart = v.vin ? `VIN: ${v.vin}` : (v.stockNumber ? `Stock#${v.stockNumber}` : 'VIN: <VIN>');
      const miles = v.milesPerYear || 10000;
      const overage = v.overagePerMile || '0.15';
      const parts = [];
      parts.push(`(${i + 1}) Lease ${v.year} ${make} ${v.model}${trim} ${idPart} for $${fmtMoney(v.salePrice)} a month / ${v.leaseMonths} months / $${fmtMoney(v.totalDueAtSigning || 0)} due at lease signing.`);
      parts.push(`${Number(miles).toLocaleString()} miles/year. No security deposit required.`);
      if (v.acquisitionFee !== undefined && v.acquisitionFee !== '') {
        parts.push(parseFloat(v.acquisitionFee) > 0
          ? `Monthly payment includes $${fmtMoney(v.acquisitionFee)} acquisition fee.`
          : 'No acquisition fee.');
      }
      parts.push('Excludes tax, title, license, and dealer options and charges.');
      parts.push(`Terms available on approved credit only to very well-qualified lessees through ${make} Financial Services and participating dealers.`);
      if (v.msrp) {
        const dest = v.destinationCharge ? ` plus $${fmtMoney(v.destinationCharge)} destination charge` : '';
        parts.push(`Lease example based on vehicle MSRP of $${fmtMoney(v.msrp)}${dest}.`);
      }
      if (v.capitalizedCost) parts.push(`Payment based on capitalized cost of $${fmtMoney(v.capitalizedCost)}.`);
      parts.push('Dealer contribution may vary and could affect lease payment. Individual dealer prices, other terms and offers may vary. Must lease from participating dealer\'s stock and terms are subject to vehicle availability.');
      parts.push(`Lessee responsible for maintenance, excess wear and use, and will pay $${overage} per mile for all mileage over ${Number(miles).toLocaleString()} miles per year.`);
      if (v.purchaseOption) parts.push(`Purchase option at lease end for $${fmtMoney(v.purchaseOption)} plus tax.`);
      if (v.dispositionFee) parts.push(`$${fmtMoney(v.dispositionFee)} disposition fee is due at lease end.`);
      parts.push(`Void where prohibited. Offer ends ${dropEnd} and you must take delivery prior to expiration of offer. See participating ${make} Dealer for complete details.`);
      return parts.join(' ');
    }
    // Finance format (Fort Myers / Tampa Bay pattern)
    const payment = calcMonthlyPayment(v.salePrice, v.down, v.apr, v.months);
    const perK = calcPerThousand(v.apr, v.months);
    const price = parseFloat(String(v.salePrice).replace(/[$,]/g, '')) || 0;
    const financed = Math.round(price * (1 - (parseFloat(v.down) || 0) / 100));
    const idPart = v.stockNumber ? `Stock#${v.stockNumber}` : (v.vin ? `VIN: ${v.vin}` : 'Stock#<STOCK>');
    return `(${i + 1}) ${v.year} ${make} ${v.model}${trim} for $${payment} per month (${idPart} // Sale Price: $${fmtMoney(v.salePrice)} // ${v.apr}% APR for ${v.months} months // ${v.down}% down // Amount Financed: $${fmtMoney(financed)} // $${perK.toFixed(2)} per month per $1,000 financed // For well-qualified buyers only // ${perOfferFeeSentence()})`;
  });

  // Global fee/accuracy block (Morgan-style)
  const globalParts = [];
  if (preDelivery || privateTag || eFiling || docFee) {
    globalParts.push(`The pre-delivery service charge${preDelivery ? ` of $${fmtMoney(preDelivery)}` : ''}${privateTag ? `, private tag agency fee of $${fmtMoney(privateTag)},` : ','} and electronic registration filing fee${eFiling ? ` of $${fmtMoney(eFiling)}` : ''} included in the advertised price are charges that represent costs and profits to the dealer for items such as inspecting, cleaning, and adjusting vehicles, and preparing documents related to the sale.`);
  }
  globalParts.push('All vehicle offers are with approved credit subject to lender\'s approval. Acceptance of conditional offers made available by the manufacturer or manufacturer captive lender(s) may result in a different sale price. We strive to update our inventory regularly, but there may be a delay between the sale of a vehicle and inventory updates. While every reasonable effort has been made to ensure the accuracy of this information, the dealership is not responsible for errors or omissions on this mailer. All specific payment and leasing offers are for well qualified buyers with approved credit and are mutually exclusive from any other promotional offers. Cannot combine any offers. See dealer for complete details.');

  // Prize block (unchanged legal structure)
  const prizeLines = prizes
    .filter((p) => p.prize)
    .map((p, i) => {
      const prizeName = p.prize === 'Other' ? (p.custom || 'Custom Prize') : p.prize;
      const val = p.value ? ` (Valued at ${p.value})` : '';
      const odds = p.odds ? ` ${p.odds}` : '';
      return `#${i + 1} ${prizeName}${val}${odds}`;
    });

  const prizeBlock = `^NO PURCHASE NECESSARY. Match your winning number to the Prize Board at the dealership to determine what prize you have won. Odds of winning ${prizeLines.join(' ')}. All taxes are the responsibility of the prize winner(s). Contest begins ${dropStart} and ends ${dropEnd}. Everyone must register to receive their prize. Purchase does not increase chance of winning. Contest open to legal US residents age 18 or older with a valid driver's license who received an original mail piece via US mail. Participants agree to all contest rules. See dealer for complete contest rules. ${dealershipInfo?.name || '<Client Name>'} and associates, mail house, associated sponsors or agencies, and their family members and members of same household are ineligible. Addressee must redeem original mail piece in person by close of business ${dropEnd}. Any unclaimed prizes will not be awarded. All photos are for illustration purposes only. Vehicles are subject to prior sale. Void where prohibited by law. All offers end ${dropEnd}.`;

  return `${vehicleLines.join(' ')} ${globalParts.join(' ')} ${prizeBlock}`;
};

// ---------------------------------------------------------------------------
// FACEBOOK AD DISCLAIMER BUILDER (used from ClientProfile)
// Formats mirror the approved Facebook disclaimers
// (Mazda of Gainesville, Lake City CDJR).
// ---------------------------------------------------------------------------

export const FB_GENERIC_DISCLAIMER = `*Price listed does not include sales tax, tag, title, registration, and any other government-related fees. The pre-delivery service charge, private tag agency fee, and electronic registration filing fee included in the advertised price are charges that represent costs and profits to the dealer for items such as inspecting, cleaning, and adjusting vehicles, and preparing documents related to the sale. Acceptance of conditional offers made available by the manufacturer or manufacturer captive lender/s may result in a different sale price. We strive to update our inventory regularly, but there may be a delay between the sale of a vehicle and inventory updates. While every reasonable effort has been made to ensure the accuracy of this information, the dealership is not responsible for errors or omissions on this site. See dealer for complete details.`;

// Fee sentence shared by the FB offer blocks
const fbFeeSentence = (fees = {}) => {
  const pre = parseFloat(fees.preDeliveryFee) || 0;
  const tag = parseFloat(fees.privateTagFee) || 0;
  const efile = parseFloat(fees.electronicFilingFee) || 0;
  if (!pre && !tag && !efile) {
    return 'The pre-delivery service charge, private tag agency fee, and electronic registration filing fee are included in the monthly advertised payment structure.';
  }
  return `The pre-delivery service charge of $${fmtMoney(pre)}, private tag agency fee of $${fmtMoney(tag)}, and electronic registration filing fee of $${fmtMoney(efile)} are included in the monthly advertised payment structure.`;
};

// Monthly payment when down payment is a dollar amount due at signing
export const calcPaymentFromDue = (salePrice, dueAtSigning, apr, months) => {
  const price = parseFloat(String(salePrice).replace(/[$,]/g, '')) || 0;
  const due = parseFloat(String(dueAtSigning).replace(/[$,]/g, '')) || 0;
  const principal = price - due;
  const monthlyRate = (parseFloat(apr) || 0) / 100 / 12;
  if (!months || principal <= 0) return 0;
  const payment = monthlyRate === 0
    ? principal / months
    : (monthlyRate * principal) / (1 - Math.pow(1 + monthlyRate, -months));
  return isNaN(payment) ? 0 : Math.round(payment);
};

/**
 * Builds the numbered "Special Disclaimer" for Facebook ads.
 * config = {
 *   fees: { preDeliveryFee, privateTagFee, electronicFilingFee },
 *   endDate: 'YYYY-MM-DD',
 *   zeroDown: { enabled },
 *   noPayments90: { enabled },
 *   zeroApr: { enabled, year, make, model, trim, vin, months, financeCompany },
 *   paymentExample: { enabled, condition, year, make, model, trim, stockNumber,
 *                     salePrice, apr, months, dueAtSigning },
 * }
 */
export const buildFbSpecialDisclaimer = (config = {}) => {
  const fees = config.fees || {};
  const blocks = [];

  if (config.zeroDown?.enabled) {
    blocks.push(
      `Drive with $0 down payment. For well-qualified buyers only. Subject to lender's approval. $0 down excludes tax, tag, title, registration and any other government-related fees. ${fbFeeSentence(fees)} See dealer for details.`
    );
  }

  if (config.noPayments90?.enabled) {
    blocks.push(
      `Make no payments for 90 days with approved credit, subject to lender's approval. Interest may accrue from the date of purchase unless otherwise stated by the lender. Not all buyers will qualify.`
    );
  }

  if (config.zeroApr?.enabled) {
    const z = config.zeroApr;
    const make = z.make || '<MAKE>';
    const months = parseInt(z.months, 10) || 0;
    const perK = months ? (1000 / months) : 0;
    const vinPart = z.vin ? ` VIN:${z.vin}` : '';
    const trimPart = z.trim ? ` ${z.trim}` : '';
    const financeCompany = z.financeCompany || `${make} Financial Services`;
    blocks.push(
      `0% Annual Percentage Rate (APR) available on approved credit only for very well-qualified customers when financing a new ${z.year || ''} ${make} ${z.model || ''}${trimPart}${vinPart} through ${financeCompany} at participating ${make} Dealers. Not all customers will qualify for credit approval or advertised APR. Amount of down payment and other factors may affect qualification. Dealer contribution may vary and could affect purchase price and amount to be financed. ${months || '<TERM>'} months at $${perK ? perK.toFixed(2) : '<RATE>'} per month per $1000 financed with $0 down at participating ${make} Dealers. Tax, title, license, other options, loan origination fees and dealer fees may apply.`
    );
  }

  if (config.paymentExample?.enabled) {
    const p = config.paymentExample;
    const months = parseInt(p.months, 10) || 0;
    const payment = calcPaymentFromDue(p.salePrice, p.dueAtSigning, p.apr, months);
    const perK = calcPerThousand(p.apr, months);
    const conditionPart = p.condition && p.condition !== 'New' ? `${p.condition} ` : '';
    const trimPart = p.trim ? ` ${p.trim}` : '';
    const idPart = p.stockNumber ? `Stock#${p.stockNumber}` : 'Stock#<STOCK>';
    blocks.push(
      `Payments as low as $${payment} per month example: ${conditionPart}${p.year || ''} ${p.make || ''} ${p.model || ''}${trimPart} // ${idPart} // $${payment} per mo for ${months || '<TERM>'} mos // ${p.apr || '<APR>'}% APR // $${fmtMoney(p.dueAtSigning || 0)} due at signing // Sale Price: $${fmtMoney(p.salePrice || 0)} // $${perK.toFixed(2)} per month for every $1,000 financed // For well-qualified buyers only // ${fbFeeSentence(fees)}`
    );
  }

  const numbered = blocks.map((text, i) => `(${i + 1}) ${text}`).join(' ');

  const endDateText = config.endDate ? fmtLongDate(config.endDate).toUpperCase() : '<END DATE>';
  const closing = ` Vehicles shown for illustration purposes only. NOT ALL BUYERS WILL QUALIFY. OFFERS MAY NOT BE COMBINED UNLESS SPECIFIED. SUBJECT TO LENDER APPROVAL. SEE DEALER FOR COMPLETE DETAILS. ALL OFFERS END ${endDateText}.`;

  return numbered ? numbered + closing : '';
};